import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = window.location.origin || '';

export const GooglePlacesAutocomplete = ({ 
  value, 
  onChange, 
  onPlaceSelect,
  placeholder = "Search for a location...",
  className 
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Update internal value when external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchPlaces = async (query) => {
    if (!query || query.length < 3) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/places/autocomplete?query=${encodeURIComponent(query)}`);
      const data = await res.json();
      setPredictions(data.predictions || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error fetching place predictions:', error);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    setSelectedPlace(null);

    // Debounce the search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  const handleSelectPlace = async (prediction) => {
    setInputValue(prediction.description);
    onChange?.(prediction.description);
    setShowDropdown(false);
    setPredictions([]);

    // Fetch place details to get the Google Maps URL
    try {
      const res = await fetch(`${API_URL}/api/places/details/${prediction.place_id}`);
      if (res.ok) {
        const details = await res.json();
        setSelectedPlace(details);
        onPlaceSelect?.({
          address: prediction.description,
          place_id: prediction.place_id,
          google_maps_url: details.google_maps_url,
          formatted_address: details.formatted_address,
          location: details.location
        });
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.('');
    setSelectedPlace(null);
    setPredictions([]);
    onPlaceSelect?.(null);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={cn("pl-10 pr-10", className)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {!loading && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Predictions Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id || index}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-muted flex items-start gap-3 border-b last:border-b-0"
            >
              <MapPin className="h-4 w-4 mt-0.5 text-ux-cyan flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{prediction.main_text}</p>
                {prediction.secondary_text && (
                  <p className="text-xs text-muted-foreground truncate">{prediction.secondary_text}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected Place Info */}
      {selectedPlace && selectedPlace.google_maps_url && (
        <p className="mt-1 text-xs text-muted-foreground">
          <a 
            href={selectedPlace.google_maps_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-ux-cyan hover:underline"
          >
            View on Google Maps
          </a>
        </p>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
